import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bars3Icon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { createSlugWithId } from '../utils/slugUtils';

type SearchResult = {
  id: string;
  title: string;
  link: string;
  type: 'Flashcard' | 'Quiz' | 'Match' | 'Study';
};

const getSearchResultTheme = (type: SearchResult['type']) => {
  switch (type) {
    case 'Flashcard':
      return {
        icon: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
        text: 'text-blue-700 dark:text-blue-300',
        label: 'text-blue-700/80 dark:text-blue-300/80',
      };
    case 'Quiz':
      return {
        icon: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
        text: 'text-purple-700 dark:text-purple-300',
        label: 'text-purple-700/80 dark:text-purple-300/80',
      };
    case 'Match':
      return {
        icon: 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300',
        text: 'text-pink-700 dark:text-pink-300',
        label: 'text-pink-700/80 dark:text-pink-300/80',
      };
    case 'Study':
      return {
        icon: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',
        text: 'text-teal-700 dark:text-teal-300',
        label: 'text-teal-700/80 dark:text-teal-300/80',
      };
    default:
      return {
        icon: 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300',
        text: 'text-gray-700 dark:text-gray-300',
        label: 'text-gray-700/80 dark:text-gray-300/80',
      };
  }
};

const Navbar: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const handleLogoClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedValue = searchValue.trim();
    setSearchResults([]);

    if (!trimmedValue) {
      navigate('/search');
      return;
    }

    navigate(`/search?q=${encodeURIComponent(trimmedValue)}`);
  };

  const handleSearchResultSelect = (link: string) => {
    setSearchResults([]);
    setSearchValue('');
    setMobileMenuOpen(false);
    navigate(link);
  };

  useEffect(() => {
    const trimmedValue = searchValue.trim();

    if (!trimmedValue) {
      setSearchResults([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await api.get('/public/search', {
          params: {
            q: trimmedValue,
            type: 'all',
            page: 1,
            limit: 5
          }
        });

        const mappedResults = (response.data?.content || [])
          .map((item: any): SearchResult | null => {
            if (item.contentType === 'deck' || item.name) {
              return {
                id: item._id,
                title: item.name,
                link: `/flashcards/${createSlugWithId(item.name, item._id)}`,
                type: 'Flashcard' as const
              };
            }

            if (item.contentType === 'quiz' || item.title) {
              return {
                id: item._id,
                title: item.title,
                link: `/quizzes/${createSlugWithId(item.title, item._id)}`,
                type: 'Quiz' as const
              };
            }

            if (item.contentType === 'match' || item.title) {
              return {
                id: item._id,
                title: item.title,
                link: `/matches/${createSlugWithId(item.title, item._id)}`,
                type: 'Match' as const
              };
            }

            return null;
          })
          .filter((item: SearchResult | null): item is SearchResult => Boolean(item));

        setSearchResults(mappedResults);
      } catch {
        setSearchResults([]);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    const handleScroll = () => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [mobileMenuOpen]);

  return (
    <header className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm sticky top-0 z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex md:justify-between items-center h-16 relative">
          <div className="absolute left-1/2 -translate-x-1/2 md:static md:left-auto md:translate-x-0 flex items-center">
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex items-center space-x-3 cursor-pointer transition-all duration-300 hover:scale-105 group"
            >
              <div className="flex flex-col items-center md:items-start">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent transition-all duration-300 group-hover:scale-105">
                  Upmindy
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium tracking-wide uppercase opacity-75 group-hover:opacity-100 transition-opacity duration-300">
                  Learning Platform
                </p>
              </div>
            </Link>
          </div>

          <div className="hidden md:flex items-center ml-auto space-x-4 lg:space-x-6">
            <form onSubmit={handleSearchSubmit} className="relative w-52 lg:w-72">
              <label htmlFor="desktop-navbar-search" className="sr-only">Pesquisar conteúdo</label>
              <div className={`flex items-center gap-2 rounded-lg border py-2 pl-3 pr-3 shadow-sm focus-within:ring-2 focus-within:ring-blue-100 ${
                isDark
                  ? 'border-gray-600 bg-gray-700 text-white focus-within:border-blue-500'
                  : 'border-gray-200 bg-gray-50 text-gray-900 focus-within:border-blue-500'
              }`}>
                <MagnifyingGlassIcon className={`h-4 w-4 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  id="desktop-navbar-search"
                  type="text"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Pesquisar conteúdo..."
                  className={`w-full border-0 bg-transparent text-sm placeholder-gray-400 outline-none ${
                    isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>

              {searchValue.trim() && searchResults.length > 0 && (
                <div className={`absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border shadow-lg ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  {searchResults.map((result) => {
                    const theme = getSearchResultTheme(result.type);

                    return (
                      <button
                        key={result.id}
                        type="button"
                        onClick={() => handleSearchResultSelect(result.link)}
                        className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`flex h-7 w-7 items-center justify-center rounded-md ${theme.icon}`}>
                          <MagnifyingGlassIcon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={`block text-[10px] font-semibold uppercase tracking-[0.12em] ${theme.label}`}>
                            {result.type}
                          </span>
                          <span className={`block truncate text-sm font-medium ${theme.text}`}>{result.title}</span>
                        </span>
                      </button>
                    );
                  })}
                  <Link
                    to={`/search${searchValue.trim() ? `?q=${encodeURIComponent(searchValue.trim())}` : ''}`}
                    onClick={() => {
                      setSearchResults([]);
                      setSearchValue('');
                      setMobileMenuOpen(false);
                    }}
                    className={`block border-t px-3 py-2 text-center text-sm font-medium transition-colors ${
                      isDark
                        ? 'border-gray-700 text-blue-300 hover:bg-gray-700'
                        : 'border-gray-200 text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    veja mais
                  </Link>
                </div>
              )}
            </form>
            <Link
              to="/search"
              className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Explorar Conteúdo
            </Link>
            <Link
              to="/docs"
              className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Documentação
            </Link>
            <Link
              to="/blog"
              className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Blog
            </Link>
            {!user && (
              <Link
                to="/login"
                className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                  isDark
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Entrar
              </Link>
            )}
            <Link
              to={user ? '/home' : '/register'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              {user ? 'Ir para App' : 'Começar Grátis'}
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors ml-auto"
            aria-label="Toggle menu"
            type="button"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      <div className={`md:hidden border-t px-4 py-2 backdrop-blur-sm ${isDark ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200/80 bg-white/95'}`}>
        <form onSubmit={handleSearchSubmit} className="relative">
          <label htmlFor="mobile-navbar-search" className="sr-only">Pesquisar conteúdo</label>
          <div className={`flex items-center gap-2 rounded-lg border py-1.5 pl-3 pr-3 shadow-sm focus-within:ring-2 focus-within:ring-blue-100 ${
            isDark
              ? 'border-gray-600 bg-gray-700 text-white focus-within:border-blue-500'
              : 'border-gray-200 bg-gray-50 text-gray-900 focus-within:border-blue-500'
          }`}>
            <MagnifyingGlassIcon className={`h-4 w-4 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            <input
              id="mobile-navbar-search"
              type="text"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Pesquisar conteúdo..."
              className={`w-full border-0 bg-transparent text-sm leading-4 placeholder-gray-400 outline-none ${
                isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {searchValue.trim() && searchResults.length > 0 && (
            <div className={`absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border shadow-lg ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              {searchResults.map((result) => {
                const theme = getSearchResultTheme(result.type);

                return (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => handleSearchResultSelect(result.link)}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                      isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={`flex h-7 w-7 items-center justify-center rounded-md ${theme.icon}`}>
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[10px] font-semibold uppercase tracking-[0.12em] ${theme.label}`}>
                        {result.type}
                      </span>
                      <span className={`block truncate text-sm font-medium ${theme.text}`}>{result.title}</span>
                    </span>
                  </button>
                );
              })}
              <Link
                to={`/search${searchValue.trim() ? `?q=${encodeURIComponent(searchValue.trim())}` : ''}`}
                onClick={() => {
                  setSearchResults([]);
                  setSearchValue('');
                  setMobileMenuOpen(false);
                }}
                className={`block border-t px-3 py-2 text-center text-sm font-medium transition-colors ${
                  isDark
                    ? 'border-gray-700 text-blue-300 hover:bg-gray-700'
                    : 'border-gray-200 text-blue-600 hover:bg-gray-50'
                }`}
              >
                veja mais
              </Link>
            </div>
          )}
        </form>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t shadow-lg`}>
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/search"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                  isDark
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Explorar Conteúdo
              </Link>
              <Link
                to="/docs"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                  isDark
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Documentação
              </Link>
              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                  isDark
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Blog
              </Link>
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    isDark
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  Entrar
                </Link>
              )}
              <Link
                to={user ? '/home' : '/register'}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-base font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
              >
                {user ? 'Ir para App' : 'Começar Grátis'}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
